import React from 'react';
import { Briefcase, Monitor, Armchair, Users, Wifi, Mic, Coffee, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/office1.jpg";
import img2 from "../../assets/office2.jpg";
import img3 from "../../assets/office3.jpg";
import img4 from "../../assets/office4.jpg";
import img5 from "../../assets/office5.jpg";

// Expanded Design List
const designs = [
  { 
    id: 1, 
    title: 'The Executive Suite', 
    category: 'MD Cabin', 
    image: img1,
    desc: 'High-back leather seating with rosewood desks.'
  },
  { 
    id: 2, 
    title: 'Smart Conference Halls', 
    category: 'Boardroom', 
    image: img2,
    desc: 'Integrated AV systems and soundproof paneling.'
  },
  { 
    id: 3, 
    title: 'Luxury Home Office', 
    category: 'Residential', 
    image: img3,
    desc: 'Cozy, productive spaces for the hybrid era.' 
  },
  { 
    id: 4, 
    title: 'Collaborative Pods', 
    category: 'Open Plan', 
    image: img4,
    desc: 'Semi-private zones for team brainstorming.'
  },
  { 
    id: 5, 
    title: 'Grand Reception Lobbies', 
    category: 'Entrance', 
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80",
    desc: 'First impressions matter. Marble floors & statement walls.'
  },
  { 
    id: 6, 
    title: 'Executive Breakout Zones', 
    category: 'Lounge', 
    image: "https://images.unsplash.com/photo-1577412647305-991150c7d163?auto=format&fit=crop&q=80",
    desc: 'Coffee bars and plush seating for informal meetings.'
  },
];

const LuxuryOffice = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* 1. HERO SECTION */}
      <div
        className="relative h-[70vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-4">
             Corporate Excellence
          </h4>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6">
            Future-Ready Workspaces
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
            Designing world-class offices in <strong>Noida Corporate Hubs</strong> & <strong>Delhi</strong>. 
            Where ergonomics meet executive luxury.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-20 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
          Command Your Space
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          Your office is a reflection of your brand's authority. At <strong>Luxuriya Homes</strong>, we go beyond furniture. 
          We engineer environments that boost productivity and impress clients. Whether it's a 
          Global HQ in Gurgaon or a Private Lawyer's Chamber in Lucknow, we blend technology 
          with timeless aesthetics using premium wood, acoustic glass, and Italian leather.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY GRID */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">Workspace Portfolio</h3>
             <p className="text-gray-500">From Startups to Fortune 500 Cabins</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden shadow-xl cursor-pointer rounded-sm bg-white">
                <div className="h-72 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={`${design.title} - Office Interior Design`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="p-6 border-b-4 border-transparent group-hover:border-amber-500 transition-all">
                  <p className="text-amber-600 text-xs tracking-widest uppercase mb-2 font-bold">{design.category}</p>
                  <h4 className="text-xl font-serif text-gray-900 mb-2">{design.title}</h4>
                  <p className="text-gray-500 text-sm">{design.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. TECH & FEATURES (NEW SECTION ADDED) */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">The Intelligent Office</h3>
             <p className="text-gray-400 max-w-2xl mx-auto">We integrate modern technology seamlessly into the design.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
             <div className="p-6 border border-white/10 hover:bg-white/5 transition-all rounded-lg">
                <Wifi className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">Smart Connectivity</h4>
                <p className="text-gray-400 text-sm">Hidden cable management & wireless charging ports.</p>
             </div>
             <div className="p-6 border border-white/10 hover:bg-white/5 transition-all rounded-lg">
                <Mic className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">Acoustic Privacy</h4>
                <p className="text-gray-400 text-sm">Sound-absorbing panels for confidential meetings.</p>
             </div>
             <div className="p-6 border border-white/10 hover:bg-white/5 transition-all rounded-lg">
                <Coffee className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">Wellness Zones</h4>
                <p className="text-gray-400 text-sm">Ergonomic chairs & biophilic design (indoor plants).</p>
             </div>
             <div className="p-6 border border-white/10 hover:bg-white/5 transition-all rounded-lg">
                <Lock className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">Secure Access</h4>
                <p className="text-gray-400 text-sm">Biometric entry integration for cabins.</p>
             </div>
          </div>
        </div>
      </section>

      {/* 5. MATERIAL PALETTE (NEW SECTION) */}
      <section className="py-20 px-6 bg-white">
         <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
               <img src="https://images.unsplash.com/photo-1505409859467-3a796fd5798e?auto=format&fit=crop&q=80" alt="Office Materials" className="rounded-lg shadow-2xl" />
            </div>
            <div className="w-full md:w-1/2">
               <h3 className="text-3xl font-serif font-bold text-gray-900 mb-6">Premium Material Selection</h3>
               <ul className="space-y-6">
                  <li className="flex gap-4">
                     <div className="w-2 h-full bg-amber-500 rounded-full"></div>
                     <div>
                        <h4 className="font-bold text-lg">Solid Wood & Veneer</h4>
                        <p className="text-gray-600 text-sm">Teak and Walnut finishes for authoritative desks.</p>
                     </div>
                  </li>
                  <li className="flex gap-4">
                     <div className="w-2 h-full bg-amber-500 rounded-full"></div>
                     <div>
                        <h4 className="font-bold text-lg">Toughened Glass</h4>
                        <p className="text-gray-600 text-sm">Switchable smart glass for instant privacy in cabins.</p>
                     </div>
                  </li>
                  <li className="flex gap-4">
                     <div className="w-2 h-full bg-amber-500 rounded-full"></div>
                     <div>
                        <h4 className="font-bold text-lg">Italian Leather</h4>
                        <p className="text-gray-600 text-sm">Hand-stitched upholstery for client seating.</p>
                     </div>
                  </li>
               </ul>
            </div>
         </div>
      </section>

      {/* 6. CTA - B2B Focus */}
      <section className="py-24 px-6 text-center bg-amber-50 border-t border-amber-100">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
          Ready to Upgrade Your Headquarters?
        </h2>
        <p className="text-gray-600 mb-10 max-w-xl mx-auto text-lg">
          Get a free layout consultation for your commercial space in **Delhi NCR, Noida or Lucknow**.
        </p>
        <Link to="/contact" className="inline-block bg-neutral-900 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-2xl uppercase tracking-widest">
          Book Office Consultation
        </Link>
      </section>
    </div>
  );
};

export default LuxuryOffice;